import { Link } from "react-router-dom";
import { ArrowLeft, Target } from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800/50">
        <div className="container mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Target className="h-6 w-6 text-emerald-400" />
            <span className="font-bold text-lg">IntentDrive</span>
            <span className="text-xs text-emerald-400">.ai</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-16 max-w-4xl">
        <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-slate-400 mb-12">
          Effective Date: April 1, 2026
        </p>

        <div className="space-y-10 text-slate-300 leading-relaxed">
          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              1. Acceptance of Terms
            </h2>
            <p>
              By accessing or using the IntentDrive.ai platform ("Service"),
              located at{" "}
              <a
                href="https://intentdrive.ai"
                className="text-emerald-400 hover:underline"
              >
                intentdrive.ai
              </a>
              , you agree to be bound by these Terms of Service ("Terms"). If
              you do not agree to these Terms, you may not access or use the
              Service. These Terms apply to all visitors, users, and others who
              access or use the Service.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              2. Description of Service
            </h2>
            <p>
              IntentDrive.ai is a software-as-a-service (SaaS) platform that
              provides AI-powered chatbot and conversation tools for lead
              generation. The Service allows users to create accounts, build
              conversational chatbot flows, embed chat widgets on third-party
              websites, collect user data (such as names and email addresses),
              and route leads via webhooks and integrations.
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              3. Account Registration
            </h2>
            <p className="mb-3">
              To use certain features of the Service, you must register for an
              account. When you register, you agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                Provide accurate, current, and complete information during
                registration.
              </li>
              <li>
                Maintain and promptly update your account information to keep it
                accurate and complete.
              </li>
              <li>
                Maintain the security and confidentiality of your login
                credentials.
              </li>
              <li>
                Accept responsibility for all activities that occur under your
                account.
              </li>
              <li>
                Notify us immediately of any unauthorized use of your account.
              </li>
            </ul>
            <p className="mt-3">
              You must be at least 18 years of age to create an account and use
              the Service.
            </p>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              4. Usage Rights and License
            </h2>
            <p className="mb-3">
              Subject to your compliance with these Terms, IntentDrive.ai grants
              you a limited, non-exclusive, non-transferable, revocable license
              to access and use the Service for your internal business purposes.
              This license includes the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Create and configure chatbot flows using our builder tools.</li>
              <li>
                Embed the provided chat widget on websites you own or operate.
              </li>
              <li>Collect and manage lead data through the platform.</li>
              <li>
                Access analytics and reporting features associated with your
                account.
              </li>
            </ul>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              5. Prohibited Uses
            </h2>
            <p className="mb-3">You agree not to use the Service to:</p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>
                Violate any applicable local, state, national, or international
                law or regulation.
              </li>
              <li>
                Send spam, unsolicited messages, or engage in any form of
                unauthorized advertising.
              </li>
              <li>
                Impersonate any person or entity, or misrepresent your
                affiliation with any person or entity.
              </li>
              <li>
                Collect or harvest personal data of end-users without their
                informed consent.
              </li>
              <li>
                Interfere with or disrupt the integrity or performance of the
                Service.
              </li>
              <li>
                Attempt to gain unauthorized access to the Service or its related
                systems or networks.
              </li>
              <li>
                Use the Service to distribute malware, viruses, or other harmful
                code.
              </li>
              <li>
                Reverse engineer, decompile, or disassemble any aspect of the
                Service.
              </li>
              <li>
                Resell, sublicense, or redistribute access to the Service without
                prior written consent.
              </li>
            </ul>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              6. Intellectual Property
            </h2>
            <p className="mb-3">
              The Service and its original content, features, and functionality
              are and will remain the exclusive property of IntentDrive.ai and
              its licensors. The Service is protected by copyright, trademark,
              and other laws. Our trademarks and trade dress may not be used in
              connection with any product or service without the prior written
              consent of IntentDrive.ai.
            </p>
            <p>
              You retain ownership of any content, data, and chatbot
              configurations you create using the Service. By using the Service,
              you grant IntentDrive.ai a limited license to host, store, and
              process your content solely for the purpose of providing and
              improving the Service.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              7. Payment and Billing
            </h2>
            <p>
              Certain features of the Service may require a paid subscription.
              By selecting a paid plan, you agree to pay the applicable fees as
              described at the time of purchase. Fees are billed in advance on a
              recurring basis (monthly or annually) and are non-refundable except
              as required by law. IntentDrive.ai reserves the right to change
              pricing with 30 days&apos; written notice.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              8. Data and Privacy
            </h2>
            <p>
              Your use of the Service is also governed by our{" "}
              <Link
                to="/privacy"
                className="text-emerald-400 hover:underline"
              >
                Privacy Policy
              </Link>
              . You are responsible for ensuring that your use of the Service
              and any collection of end-user data complies with all applicable
              privacy laws and regulations, including providing appropriate
              notices and obtaining necessary consents from end-users whose data
              you collect through chatbot interactions.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              9. Limitation of Liability
            </h2>
            <p className="mb-3">
              To the maximum extent permitted by applicable law, IntentDrive.ai
              and its directors, employees, partners, agents, suppliers, or
              affiliates shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages, including without
              limitation, loss of profits, data, use, goodwill, or other
              intangible losses, resulting from:
            </p>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Your access to or use of (or inability to access or use) the Service.</li>
              <li>Any conduct or content of any third party on the Service.</li>
              <li>Any content obtained from the Service.</li>
              <li>
                Unauthorized access, use, or alteration of your transmissions or
                content.
              </li>
            </ul>
            <p className="mt-3">
              In no event shall IntentDrive.ai's total liability exceed the
              amount you have paid to IntentDrive.ai in the twelve (12) months
              preceding the claim.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              10. Disclaimer of Warranties
            </h2>
            <p>
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis
              without warranties of any kind, whether express or implied,
              including but not limited to implied warranties of
              merchantability, fitness for a particular purpose,
              non-infringement, or course of performance. IntentDrive.ai does
              not warrant that the Service will function uninterrupted, secure,
              or available at any particular time or location, or that any
              defects or errors will be corrected.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              11. Termination
            </h2>
            <p className="mb-3">
              We may terminate or suspend your account and access to the Service
              immediately, without prior notice or liability, for any reason,
              including without limitation if you breach these Terms.
            </p>
            <p>
              Upon termination, your right to use the Service will immediately
              cease. You may request an export of your data within 30 days of
              termination by contacting us at{" "}
              <a
                href="mailto:support@intentdrive.ai"
                className="text-emerald-400 hover:underline"
              >
                support@intentdrive.ai
              </a>
              . After 30 days, we reserve the right to delete your account data.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              12. Indemnification
            </h2>
            <p>
              You agree to indemnify and hold harmless IntentDrive.ai and its
              officers, directors, employees, and agents from and against any
              claims, liabilities, damages, losses, and expenses, including
              reasonable attorneys&apos; fees, arising out of or in any way connected
              with your access to or use of the Service, your violation of these
              Terms, or your violation of any rights of a third party.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              13. Governing Law
            </h2>
            <p>
              These Terms shall be governed and construed in accordance with the
              laws of the State of Delaware, United States, without regard to its
              conflict of law provisions. Any disputes arising under or in
              connection with these Terms shall be subject to the exclusive
              jurisdiction of the courts located in Delaware.
            </p>
          </section>

          {/* 14 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              14. Changes to Terms
            </h2>
            <p>
              We reserve the right to modify or replace these Terms at any time.
              If a revision is material, we will provide at least 30 days&apos;
              notice prior to any new terms taking effect. What constitutes a
              material change will be determined at our sole discretion. Your
              continued use of the Service after any changes constitutes
              acceptance of the new Terms.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              15. Contact Us
            </h2>
            <p>
              If you have any questions about these Terms, please contact us at{" "}
              <a
                href="mailto:support@intentdrive.ai"
                className="text-emerald-400 hover:underline"
              >
                support@intentdrive.ai
              </a>
              .
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 py-8">
        <div className="container mx-auto px-6 text-center text-sm text-slate-600">
          &copy; 2026 IntentDrive.ai. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
